import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAssistantPageExtras } from '../components/assistant/AssistantContext';
import { useAuth } from '../auth/AuthContext';
import { loadRegister } from '../engine/scenarioFreeze';
import { track } from '../lib/track';
import { crmApi } from './api';

const CRM_APPLY_EVENT = 'crm-apply-outreach';
const CRM_ASK_EVENT = 'crm-ask-assistant';

const KB_ACCEPT =
  '.txt,.md,.markdown,.csv,.tsv,.json,.vtt,.srt,.log,.eml,.docx,text/plain,text/markdown,application/json';

function guessDocType(filename, text) {
  const name = (filename || '').toLowerCase();
  if (/\.(vtt|srt)$/.test(name) || /transcript|zoom|teams|otter/i.test(name)) return 'transcript';
  if (/\.eml$/.test(name) || /^from:|^subject:/im.test(text || '')) return 'email';
  return 'note';
}

const STAGES = [
  { key: 'prospect', label: 'Prospect' },
  { key: 'teaser', label: 'Teaser' },
  { key: 'portal', label: 'Portal' },
  { key: 'engaged', label: 'Engaged' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'soft_circle', label: 'Soft circle' },
  { key: 'committed', label: 'Committed' },
  { key: 'passed', label: 'Passed' },
];

function fmtPct(x) {
  if (x == null || Number.isNaN(+x)) return '—';
  return `${(+x * 100).toFixed(1)}%`;
}

function fmtMM(x) {
  if (x == null || Number.isNaN(+x)) return '—';
  return `$${Number(x).toFixed(1)}MM`;
}

const EMPTY_PROSPECT = {
  firm_name: '',
  firm_type: 'Single Family Office',
  hq_city: '',
  hq_state: '',
  tier: 'Tier 2',
  aum: '',
  fit_rationale: '',
  contact_name: '',
  contact_title: '',
  contact_email: '',
};

const EMPTY_CONTACT = {
  name: '',
  title: '',
  email: '',
  is_primary: false,
};

function firmEditFromFirm(firm) {
  return {
    firm_name: firm?.firm_name || '',
    firm_type: firm?.firm_type || '',
    hq_city: firm?.hq_city || '',
    hq_state: firm?.hq_state || '',
    tier: firm?.tier || 'Tier 2',
    aum: firm?.aum || '',
    fit_rationale: firm?.fit_rationale || '',
    wealth_origin: firm?.wealth_origin || '',
    energy_evidence: firm?.energy_evidence || '',
    example_deals: firm?.example_deals || '',
    private_credit: !!firm?.private_credit,
    notes: firm?.notes || '',
  };
}

function FirmList({
  firms,
  selectedId,
  onSelect,
  filters,
  setFilters,
  summary,
  onImport,
  onCreate,
  busy,
  kbQ,
  setKbQ,
  kbHits,
  onKbSearch,
  kbSearching,
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(EMPTY_PROSPECT);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!draft.firm_name.trim()) return;
    const contacts = [];
    if (draft.contact_name.trim()) {
      contacts.push({
        name: draft.contact_name.trim(),
        title: draft.contact_title.trim(),
        email: draft.contact_email.trim() || null,
        is_primary: true,
      });
    }
    const created = await onCreate({
      firm_name: draft.firm_name.trim(),
      firm_type: draft.firm_type.trim(),
      hq_city: draft.hq_city.trim(),
      hq_state: draft.hq_state.trim().toUpperCase(),
      tier: draft.tier,
      aum: draft.aum.trim(),
      fit_rationale: draft.fit_rationale.trim(),
      contacts,
    });
    if (created) {
      setDraft(EMPTY_PROSPECT);
      setShowCreate(false);
    }
  };

  return (
    <div className="crm-list-pane">
      <div className="crm-list-head">
        <div>
          <h2>Investor CRM</h2>
          <p>
            FINTRX equity targets · {summary?.total ?? firms.length} firms ·{' '}
            {summary?.with_portal ?? 0} portal accounts
          </p>
        </div>
        <div className="crm-list-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? 'Cancel' : 'New prospect'}
          </button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={onImport}>
            {busy ? 'Importing…' : 'Import FINTRX list'}
          </button>
        </div>
      </div>

      {showCreate && (
        <form className="crm-create-form" onSubmit={submitCreate}>
          <div className="crm-label">New prospect</div>
          <label className="crm-field">
            Firm name
            <input
              required
              value={draft.firm_name}
              disabled={busy}
              onChange={(e) => setDraft((d) => ({ ...d, firm_name: e.target.value }))}
              placeholder="e.g. Riverbend Capital"
            />
          </label>
          <div className="crm-create-grid">
            <label className="crm-field">
              Type
              <input
                value={draft.firm_type}
                disabled={busy}
                onChange={(e) => setDraft((d) => ({ ...d, firm_type: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Tier
              <select
                value={draft.tier}
                disabled={busy}
                onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value }))}
              >
                <option value="Tier 1">Tier 1</option>
                <option value="Tier 2">Tier 2</option>
                <option value="Tier 3">Tier 3</option>
              </select>
            </label>
            <label className="crm-field">
              City
              <input
                value={draft.hq_city}
                disabled={busy}
                onChange={(e) => setDraft((d) => ({ ...d, hq_city: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              State
              <input
                value={draft.hq_state}
                disabled={busy}
                maxLength={8}
                onChange={(e) => setDraft((d) => ({ ...d, hq_state: e.target.value }))}
                placeholder="TX"
              />
            </label>
          </div>
          <label className="crm-field">
            Fit thesis
            <textarea
              rows={2}
              value={draft.fit_rationale}
              disabled={busy}
              onChange={(e) => setDraft((d) => ({ ...d, fit_rationale: e.target.value }))}
              placeholder="Why this office for Eagle equity…"
            />
          </label>
          <div className="crm-create-grid">
            <label className="crm-field">
              Contact name
              <input
                value={draft.contact_name}
                disabled={busy}
                onChange={(e) => setDraft((d) => ({ ...d, contact_name: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Contact email
              <input
                type="email"
                value={draft.contact_email}
                disabled={busy}
                onChange={(e) => setDraft((d) => ({ ...d, contact_email: e.target.value }))}
              />
            </label>
          </div>
          <div className="crm-create-actions">
            <button type="submit" className="btn-primary" disabled={busy || !draft.firm_name.trim()}>
              {busy ? 'Creating…' : 'Create prospect'}
            </button>
          </div>
        </form>
      )}

      {summary?.by_tier && (
        <div className="crm-tier-strip">
          {['Tier 1', 'Tier 2', 'Tier 3'].map((t) => (
            <button
              key={t}
              type="button"
              className={`crm-tier-chip ${filters.tier === t ? 'active' : ''}`}
              onClick={() => setFilters((f) => ({ ...f, tier: f.tier === t ? '' : t }))}
            >
              {t} · {summary.by_tier[t] || 0}
            </button>
          ))}
        </div>
      )}

      <div className="crm-filters">
        <input
          type="search"
          placeholder="Search firm, thesis, city…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
        />
        <div className="crm-kb-search-row">
          <input
            type="search"
            placeholder="Search notes & transcripts…"
            value={kbQ}
            disabled={kbSearching}
            onChange={(e) => setKbQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onKbSearch();
              }
            }}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={kbSearching || !kbQ.trim()}
            onClick={onKbSearch}
          >
            {kbSearching ? '…' : 'KB'}
          </button>
        </div>
        {kbHits.length > 0 && (
          <ul className="crm-kb-hits crm-kb-hits-global">
            {kbHits.slice(0, 5).map((h) => (
              <li key={h.chunk_id}>
                <button
                  type="button"
                  className="crm-kb-hit-link"
                  onClick={() => onSelect(h.firm_id)}
                >
                  <div className="crm-activity-meta">
                    <span className="crm-activity-kind">{h.doc_type || 'doc'}</span>
                    <span>{h.firm_name}</span>
                  </div>
                  <strong>{h.title}</strong>
                  <p>{(h.text || '').slice(0, 160)}{(h.text || '').length > 160 ? '…' : ''}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
        <select
          value={filters.stage}
          onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
        >
          <option value="">All stages</option>
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
              {summary?.by_stage?.[s.key] != null ? ` (${summary.by_stage[s.key]})` : ''}
            </option>
          ))}
        </select>
        <select
          value={filters.state}
          onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
        >
          <option value="">All states</option>
          <option value="TX">TX</option>
          <option value="OK">OK</option>
          <option value="CO">CO</option>
          <option value="NY">NY</option>
          <option value="FL">FL</option>
          <option value="CA">CA</option>
        </select>
      </div>

      <div className="crm-firm-rows">
        {firms.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`crm-firm-row ${selectedId === f.id ? 'active' : ''}`}
            onClick={() => onSelect(f.id)}
          >
            <div className="crm-firm-row-top">
              <span className="crm-firm-name">{f.firm_name}</span>
              <span className={`crm-stage-pill stage-${f.stage}`}>{f.stage_label}</span>
            </div>
            <div className="crm-firm-row-meta">
              <span>{f.tier}</span>
              <span>
                {f.hq_city}, {f.hq_state}
              </span>
              <span>{f.firm_type}</span>
              {f.has_pack && <span className="crm-pack-dot">pack</span>}
            </div>
          </button>
        ))}
        {!firms.length && (
          <div className="crm-empty">No firms match. Import the FINTRX target list to begin.</div>
        )}
      </div>
    </div>
  );
}

function FirmDetail({ firm, isAdmin, userId, onRefresh, onDeleted, notice, setNotice, setError }) {
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const [note, setNote] = useState('');
  const [noteKind, setNoteKind] = useState('note');
  const [noteTitle, setNoteTitle] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [kbQ, setKbQ] = useState('');
  const [kbHits, setKbHits] = useState([]);
  const [kbSearching, setKbSearching] = useState(false);
  const fileInputRef = useRef(null);
  const [stage, setStage] = useState(firm?.stage || 'prospect');
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const [contactId, setContactId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingFirm, setEditingFirm] = useState(false);
  const [firmEdit, setFirmEdit] = useState(null);
  const [addingContact, setAddingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactEdit, setContactEdit] = useState(null);
  const register = useMemo(() => loadRegister(), [firm?.id]);
  const selectedContact = useMemo(
    () => (firm?.contacts || []).find((c) => c.id === contactId) || firm?.contacts?.[0] || null,
    [firm?.contacts, contactId],
  );
  const publishedPack = useMemo(
    () =>
      (firm?.packs || []).find(
        (pack) => pack.status === 'published' && pack.case?.state === 'approved',
      ) || null,
    [firm?.packs],
  );

  useEffect(() => {
    setStage(firm?.stage || 'prospect');
    setNote('');
    setNoteKind('note');
    setNoteTitle('');
    setKbQ('');
    setKbHits([]);
    setEditingFirm(false);
    setFirmEdit(null);
    setAddingContact(false);
    setContactDraft(EMPTY_CONTACT);
    setEditingContactId(null);
    setContactEdit(null);
    const primary = (firm?.contacts || []).find((c) => c.is_primary) || firm?.contacts?.[0];
    setContactId(primary?.id || null);
  }, [firm?.id]);

  useEffect(() => {
    setStage(firm?.stage || 'prospect');
  }, [firm?.stage]);

  useEffect(() => {
    setContactId((prev) => {
      if (prev && (firm?.contacts || []).some((c) => c.id === prev)) return prev;
      const primary = (firm?.contacts || []).find((c) => c.is_primary) || firm?.contacts?.[0];
      return primary?.id || null;
    });
  }, [firm?.contacts]);

  useEffect(() => {
    if (!firm?.id) return;
    let cancelled = false;
    crmApi
      .outreachDraft(firm.id, contactId)
      .then((res) => {
        if (!cancelled) setDraft(res.draft || { subject: '', body: '' });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [firm?.id, contactId]);

  useEffect(() => {
    if (!firm) {
      clearPageExtras();
      return undefined;
    }
    setPageExtras({
      surface: 'crm',
      firm: {
        id: firm.id,
        firm_name: firm.firm_name,
        firm_type: firm.firm_type,
        hq_city: firm.hq_city,
        hq_state: firm.hq_state,
        aum: firm.aum,
        tier: firm.tier,
        wealth_origin: firm.wealth_origin,
        energy_evidence: firm.energy_evidence,
        example_deals: firm.example_deals,
        private_credit: firm.private_credit,
        fit_rationale: firm.fit_rationale,
        stage: firm.stage,
        notes: firm.notes,
      },
      contact: selectedContact
        ? {
            id: selectedContact.id,
            name: selectedContact.name,
            title: selectedContact.title,
            email: selectedContact.email,
          }
        : null,
      outreachDraft: draft,
      packs: (firm.packs || []).map((p) => ({
        id: p.id,
        label: p.label,
        status: p.status,
        content_hash: p.content_hash,
      })),
      recentActivity: (firm.activities || []).slice(0, 8).map((a) => ({
        kind: a.kind,
        subject: a.subject,
        created_at: a.created_at,
      })),
      documents: (firm.documents || []).slice(0, 12).map((d) => ({
        id: d.id,
        title: d.title,
        doc_type: d.doc_type,
        filename: d.filename,
        chunk_count: d.chunk_count,
        created_at: d.created_at,
      })),
    });
    return () => clearPageExtras();
  }, [firm, selectedContact, draft, setPageExtras, clearPageExtras]);

  useEffect(() => {
    const onApply = (ev) => {
      const next = ev?.detail;
      if (!next || (!next.subject && !next.body)) return;
      setDraft((d) => ({
        subject: next.subject || d.subject,
        body: next.body || d.body,
      }));
      setNotice('Assistant draft inserted into outreach editor — review before sending.');
      track('crm_outreach_draft_applied', { firm_id: firm?.id || null });
    };
    window.addEventListener(CRM_APPLY_EVENT, onApply);
    return () => window.removeEventListener(CRM_APPLY_EVENT, onApply);
  }, [firm?.id, setNotice]);

  const askAgent = (prompt) => {
    window.dispatchEvent(new CustomEvent(CRM_ASK_EVENT, { detail: { prompt } }));
    track('crm_ask_assistant', { firm_id: firm?.id || null, prompt_kind: prompt.slice(0, 40) });
  };

  if (!firm) {
    return (
      <div className="crm-detail-pane crm-empty-detail">
        <h3>Select a firm</h3>
        <p>
          Pipeline starts with energy-native SFOs (Tier 1), then real-asset total-return offices
          (Tier 2), then credit-adjacent capital (Tier 3). Prepare a capital pack from Decision,
          personalize outreach, then invite to the investor portal. Use the assistant (lower right)
          to research each office and draft / review teaser email.
        </p>
      </div>
    );
  }

  const saveStage = async (next) => {
    setBusy(true);
    setError('');
    try {
      await crmApi.patchFirm(firm.id, { stage: next });
      track('crm_stage_changed', { stage: next, firm_id: firm.id });
      setStage(next);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteFirm = async () => {
    const portalN = (firm.portal_users || []).length;
    const warn = portalN
      ? `\n\nThis will also deactivate ${portalN} portal account${portalN === 1 ? '' : 's'}.`
      : '';
    if (
      !window.confirm(
        `Delete prospect “${firm.firm_name}”? This removes contacts, activity, packs, and knowledge.${warn}`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await crmApi.deleteFirm(firm.id);
      track('crm_firm_deleted', {
        firm_id: firm.id,
        portal_users_deactivated: res.portal_users_deactivated || 0,
      });
      setNotice(`Deleted ${firm.firm_name}.`);
      onDeleted?.(firm.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEditFirm = () => {
    setFirmEdit(firmEditFromFirm(firm));
    setEditingFirm(true);
  };

  const saveFirmEdit = async (e) => {
    e?.preventDefault?.();
    if (!firmEdit?.firm_name?.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await crmApi.patchFirm(firm.id, {
        firm_name: firmEdit.firm_name.trim(),
        firm_type: firmEdit.firm_type.trim(),
        hq_city: firmEdit.hq_city.trim(),
        hq_state: firmEdit.hq_state.trim().toUpperCase(),
        tier: firmEdit.tier,
        aum: firmEdit.aum.trim(),
        fit_rationale: firmEdit.fit_rationale.trim(),
        wealth_origin: firmEdit.wealth_origin.trim(),
        energy_evidence: firmEdit.energy_evidence.trim(),
        example_deals: firmEdit.example_deals.trim(),
        private_credit: !!firmEdit.private_credit,
        notes: firmEdit.notes.trim(),
      });
      track('crm_firm_updated', { firm_id: firm.id });
      setEditingFirm(false);
      setFirmEdit(null);
      setNotice('Firm profile updated.');
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveNewContact = async (e) => {
    e?.preventDefault?.();
    if (!contactDraft.name.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const created = await crmApi.addContact(firm.id, {
        name: contactDraft.name.trim(),
        title: contactDraft.title.trim(),
        email: contactDraft.email.trim() || null,
        is_primary: !!contactDraft.is_primary,
      });
      track('crm_contact_added', { firm_id: firm.id, contact_id: created.id });
      setAddingContact(false);
      setContactDraft(EMPTY_CONTACT);
      setContactId(created.id);
      setNotice(`Added contact ${created.name}.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEditContact = (c) => {
    setEditingContactId(c.id);
    setContactEdit({
      name: c.name || '',
      title: c.title || '',
      email: c.email || '',
      is_primary: !!c.is_primary,
    });
    setAddingContact(false);
  };

  const saveContactEdit = async (e) => {
    e?.preventDefault?.();
    if (!editingContactId || !contactEdit?.name?.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const updated = await crmApi.patchContact(firm.id, editingContactId, {
        name: contactEdit.name.trim(),
        title: contactEdit.title.trim(),
        email: contactEdit.email.trim() || null,
        is_primary: !!contactEdit.is_primary,
      });
      track('crm_contact_updated', { firm_id: firm.id, contact_id: updated.id });
      setEditingContactId(null);
      setContactEdit(null);
      setNotice(`Updated contact ${updated.name}.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Remove contact “${c.name}”?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await crmApi.deleteContact(firm.id, c.id);
      track('crm_contact_removed', { firm_id: firm.id, contact_id: c.id });
      if (contactId === c.id) setContactId(null);
      if (editingContactId === c.id) {
        setEditingContactId(null);
        setContactEdit(null);
      }
      setNotice(`Removed ${c.name}.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const kind = noteKind || 'note';
      const subject =
        noteTitle.trim() ||
        (kind === 'transcript' ? 'Call transcript' : kind === 'email' ? 'Email / notes' : 'Note');
      await crmApi.addKnowledge(firm.id, {
        doc_type: kind,
        title: subject,
        body: note.trim(),
        contact_id: contactId,
        source: 'paste',
      });
      track('crm_knowledge_pasted', { firm_id: firm.id, doc_type: kind, chars: note.trim().length });
      setNote('');
      setNoteTitle('');
      setNotice(`${subject} indexed in knowledge base.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const ingestFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let lastType = 'transcript';
      for (const file of files) {
        const docType = guessDocType(file.name, '');
        lastType = docType;
        await crmApi.uploadKnowledge(firm.id, {
          file,
          docType,
          title: file.name,
          contactId,
        });
        track('crm_knowledge_uploaded', {
          firm_id: firm.id,
          doc_type: docType,
          filename: file.name,
          bytes: file.size,
        });
      }
      setNotice(
        files.length === 1
          ? `Uploaded “${files[0].name}” and indexed for search.`
          : `Uploaded ${files.length} files and indexed for search.`,
      );
      setNoteKind(lastType);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const runKbSearch = async () => {
    const q = kbQ.trim();
    if (!q) return;
    setKbSearching(true);
    setError('');
    try {
      const res = await crmApi.searchKnowledge({ q, firm_id: firm.id, limit: 6 });
      setKbHits(res.hits || []);
      track('crm_knowledge_searched', {
        firm_id: firm.id,
        q_len: q.length,
        hits: (res.hits || []).length,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setKbSearching(false);
    }
  };

  const attachFromRegister = async (scenarioId) => {
    const freeze = register.find((f) => f.scenarioId === scenarioId);
    if (!freeze) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const created = await crmApi.createCase({ snapshot: freeze });
      const capitalCase = created?.case;
      if (!capitalCase?.id) throw new Error('Server did not create a capital case');
      const pack = await crmApi.attachPack(firm.id, {
        label: freeze.label || 'Working case',
        case_id: capitalCase.id,
        outreach_subject: draft.subject,
        outreach_body: draft.body,
      });
      track('crm_pack_attached', {
        firm_id: firm.id,
        case_id: capitalCase.id,
        status: capitalCase.state,
      });
      setNotice(
        `Draft case “${freeze.label}” attached with server digest ${capitalCase.digest?.slice(0, 12) || '—'}. An independent admin must approve it before publication.`,
      );
      if (pack?.id) {
        // keep selected; refresh below
      }
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveCase = async (caseId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await crmApi.approveCase(caseId);
      track('crm_case_approved', { firm_id: firm.id, case_id: caseId });
      setNotice(`Capital case approved with digest ${res?.case?.digest?.slice(0, 12) || '—'}.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendOutreach = async () => {
    if (!publishedPack) {
      setError('Publish a current approved capital case before sending outreach.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await crmApi.sendOutreach(firm.id, {
        contact_id: contactId,
        subject: draft.subject,
        body: draft.body,
        pack_id: publishedPack.id,
        include_portal_hint: true,
        advance_stage: true,
      });
      track('crm_outreach_sent', { firm_id: firm.id, contact_id: contactId || null });
      setNotice(`Outreach emailed to ${res.to}.`);
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const publishPack = async (packId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await crmApi.publishPack(firm.id, packId);
      track('crm_pack_published', { firm_id: firm.id, pack_id: packId });
      setNotice('Pack published — visible in investor portal.');
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const invitePortal = async () => {
    if (!publishedPack) {
      setError('Publish a current approved capital case before inviting an investor.');
      return;
    }
    const contact = firm.contacts?.find((c) => c.id === contactId) || firm.contacts?.[0];
    if (!contact?.email) {
      setError('Select a contact with an email before inviting.');
      return;
    }
    if (
      !window.confirm(
        `Invite / resend investor portal access for ${contact.email}? (Same-firm resend is idempotent.)`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await crmApi.invite(firm.id, {
        contact_id: contact.id,
        pack_id: publishedPack.id,
        send_invite: true,
        advance_stage: true,
      });
      track('crm_portal_invited', {
        firm_id: firm.id,
        contact_id: contact.id,
        resent: !!res?.resent,
      });
      setNotice(
        res?.resent
          ? `Portal invite resent (same account).`
          : `Portal invite sent.`,
      );
      await onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="crm-detail-pane">
      <div className="crm-detail-head">
        <div>
          <div className="crm-detail-kicker">
            {firm.tier} · {firm.firm_type} · {firm.hq_city}, {firm.hq_state}
            {firm.aum ? ` · AUM ${firm.aum}` : ''}
          </div>
          <h2>{firm.firm_name}</h2>
        </div>
        <div className="crm-detail-actions">
          <select
            className="crm-stage-select"
            value={stage}
            disabled={busy}
            onChange={(e) => saveStage(e.target.value)}
          >
            {STAGES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => (editingFirm ? setEditingFirm(false) : startEditFirm())}
          >
            {editingFirm ? 'Cancel edit' : 'Edit firm'}
          </button>
          <button type="button" className="btn-danger" disabled={busy} onClick={deleteFirm}>
            Delete
          </button>
        </div>
      </div>

      {notice && <div className="auth-ok">{notice}</div>}

      {editingFirm && firmEdit ? (
        <section className="crm-section">
          <h3>Edit firm</h3>
          <form className="crm-edit-form" onSubmit={saveFirmEdit}>
            <label className="crm-field">
              Firm name
              <input
                required
                value={firmEdit.firm_name}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, firm_name: e.target.value }))}
              />
            </label>
            <div className="crm-create-grid">
              <label className="crm-field">
                Type
                <input
                  value={firmEdit.firm_type}
                  disabled={busy}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, firm_type: e.target.value }))}
                />
              </label>
              <label className="crm-field">
                Tier
                <select
                  value={firmEdit.tier}
                  disabled={busy}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, tier: e.target.value }))}
                >
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                  <option value="Tier 3">Tier 3</option>
                </select>
              </label>
              <label className="crm-field">
                City
                <input
                  value={firmEdit.hq_city}
                  disabled={busy}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, hq_city: e.target.value }))}
                />
              </label>
              <label className="crm-field">
                State
                <input
                  value={firmEdit.hq_state}
                  disabled={busy}
                  maxLength={8}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, hq_state: e.target.value }))}
                />
              </label>
              <label className="crm-field">
                AUM
                <input
                  value={firmEdit.aum}
                  disabled={busy}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, aum: e.target.value }))}
                />
              </label>
              <label className="crm-field crm-field-check">
                <input
                  type="checkbox"
                  checked={!!firmEdit.private_credit}
                  disabled={busy}
                  onChange={(e) => setFirmEdit((d) => ({ ...d, private_credit: e.target.checked }))}
                />
                Private credit
              </label>
            </div>
            <label className="crm-field">
              Fit thesis
              <textarea
                rows={3}
                value={firmEdit.fit_rationale}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, fit_rationale: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Wealth origin
              <textarea
                rows={2}
                value={firmEdit.wealth_origin}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, wealth_origin: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Energy / real-asset evidence
              <textarea
                rows={2}
                value={firmEdit.energy_evidence}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, energy_evidence: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Example deals
              <textarea
                rows={2}
                value={firmEdit.example_deals}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, example_deals: e.target.value }))}
              />
            </label>
            <label className="crm-field">
              Internal notes
              <textarea
                rows={2}
                value={firmEdit.notes}
                disabled={busy}
                onChange={(e) => setFirmEdit((d) => ({ ...d, notes: e.target.value }))}
              />
            </label>
            <div className="crm-create-actions">
              <button type="submit" className="btn-primary" disabled={busy || !firmEdit.firm_name.trim()}>
                {busy ? 'Saving…' : 'Save firm'}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="crm-section">
          <h3>Fit thesis</h3>
          <p className="crm-thesis">{firm.fit_rationale || '—'}</p>
          <div className="crm-evidence-grid">
            <div>
              <div className="crm-label">Wealth origin</div>
              <p>{firm.wealth_origin || '—'}</p>
            </div>
            <div>
              <div className="crm-label">Energy / real-asset evidence</div>
              <p>{firm.energy_evidence || '—'}</p>
            </div>
            <div>
              <div className="crm-label">Example deals</div>
              <p>{firm.example_deals || '—'}</p>
            </div>
            <div>
              <div className="crm-label">Private credit</div>
              <p>{firm.private_credit ? 'Yes' : 'No'}</p>
            </div>
          </div>
          {firm.notes ? (
            <div className="crm-firm-notes">
              <div className="crm-label">Internal notes</div>
              <p>{firm.notes}</p>
            </div>
          ) : null}
        </section>
      )}

      <section className="crm-section">
        <div className="crm-section-head">
          <h3>Contacts</h3>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => {
              setAddingContact((v) => !v);
              setEditingContactId(null);
              setContactEdit(null);
              if (!addingContact) setContactDraft(EMPTY_CONTACT);
            }}
          >
            {addingContact ? 'Cancel' : 'Add contact'}
          </button>
        </div>
        {addingContact && (
          <form className="crm-edit-form" onSubmit={saveNewContact}>
            <div className="crm-create-grid">
              <label className="crm-field">
                Name
                <input
                  required
                  value={contactDraft.name}
                  disabled={busy}
                  onChange={(e) => setContactDraft((d) => ({ ...d, name: e.target.value }))}
                />
              </label>
              <label className="crm-field">
                Title
                <input
                  value={contactDraft.title}
                  disabled={busy}
                  onChange={(e) => setContactDraft((d) => ({ ...d, title: e.target.value }))}
                />
              </label>
              <label className="crm-field">
                Email
                <input
                  type="email"
                  value={contactDraft.email}
                  disabled={busy}
                  onChange={(e) => setContactDraft((d) => ({ ...d, email: e.target.value }))}
                />
              </label>
              <label className="crm-field crm-field-check">
                <input
                  type="checkbox"
                  checked={!!contactDraft.is_primary}
                  disabled={busy}
                  onChange={(e) => setContactDraft((d) => ({ ...d, is_primary: e.target.checked }))}
                />
                Primary contact
              </label>
            </div>
            <div className="crm-create-actions">
              <button type="submit" className="btn-primary" disabled={busy || !contactDraft.name.trim()}>
                {busy ? 'Adding…' : 'Add contact'}
              </button>
            </div>
          </form>
        )}
        <div className="crm-contact-list">
          {(firm.contacts || []).map((c) => (
            <div key={c.id} className="crm-contact-row">
              {editingContactId === c.id && contactEdit ? (
                <form className="crm-edit-form crm-contact-edit" onSubmit={saveContactEdit}>
                  <div className="crm-create-grid">
                    <label className="crm-field">
                      Name
                      <input
                        required
                        value={contactEdit.name}
                        disabled={busy}
                        onChange={(e) => setContactEdit((d) => ({ ...d, name: e.target.value }))}
                      />
                    </label>
                    <label className="crm-field">
                      Title
                      <input
                        value={contactEdit.title}
                        disabled={busy}
                        onChange={(e) => setContactEdit((d) => ({ ...d, title: e.target.value }))}
                      />
                    </label>
                    <label className="crm-field">
                      Email
                      <input
                        type="email"
                        value={contactEdit.email}
                        disabled={busy}
                        onChange={(e) => setContactEdit((d) => ({ ...d, email: e.target.value }))}
                      />
                    </label>
                    <label className="crm-field crm-field-check">
                      <input
                        type="checkbox"
                        checked={!!contactEdit.is_primary}
                        disabled={busy}
                        onChange={(e) =>
                          setContactEdit((d) => ({ ...d, is_primary: e.target.checked }))
                        }
                      />
                      Primary contact
                    </label>
                  </div>
                  <div className="crm-contact-edit-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busy}
                      onClick={() => {
                        setEditingContactId(null);
                        setContactEdit(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !contactEdit.name.trim()}>
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                <label className={`crm-contact ${contactId === c.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="crm-contact"
                    checked={contactId === c.id}
                    onChange={() => setContactId(c.id)}
                  />
                  <div className="crm-contact-body">
                    <strong>{c.name}</strong>
                    {c.is_primary ? <span className="crm-muted"> · primary</span> : null}
                    {c.title ? <span> — {c.title}</span> : null}
                    <div className="crm-contact-email">{c.email || 'no email'}</div>
                  </div>
                  <div className="crm-contact-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busy}
                      onClick={(e) => {
                        e.preventDefault();
                        startEditContact(c);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-danger"
                      disabled={busy}
                      onClick={(e) => {
                        e.preventDefault();
                        removeContact(c);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </label>
              )}
            </div>
          ))}
          {!firm.contacts?.length && !addingContact && (
            <p className="crm-muted">No contacts yet — add one here or import from FINTRX.</p>
          )}
        </div>
      </section>

      <section className="crm-section">
        <h3>Capital pack</h3>
        <p className="crm-muted">
          Freeze a working case on the Decision desk, then attach it here. That pack becomes what
          the investor sees in their portal (sanitized KPIs + one-pager narrative).
        </p>
        {(firm.packs || []).length > 0 ? (
          <ul className="crm-pack-list">
            {firm.packs.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>{p.label}</strong>
                  <span className={`crm-stage-pill stage-${p.status === 'published' ? 'engaged' : 'prospect'}`}>
                    {p.status || 'draft'}
                  </span>
                  {p.case && (
                    <span>
                      {' '}
                      · case {p.case.state || 'draft'} · {String(p.case.digest || '').slice(0, 12)}
                    </span>
                  )}
                  <span>
                    {p.summary?.pvMM != null ? ` · PV ${fmtMM(p.summary.pvMM)}` : ''}
                    {p.summary?.irrAnnual != null ? ` · IRR ${fmtPct(p.summary.irrAnnual)}` : ''}
                  </span>
                </div>
                {isAdmin &&
                  p.case?.state === 'draft' &&
                  p.case?.created_by != null &&
                  Number(p.case.created_by) !== Number(userId) && (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => approveCase(p.case.id)}
                  >
                    Approve case
                  </button>
                )}
                {isAdmin &&
                  p.case?.state === 'draft' &&
                  Number(p.case?.created_by) === Number(userId) && (
                  <span className="crm-muted">Waiting for a different admin to approve.</span>
                )}
                {isAdmin && p.status !== 'published' && p.status !== 'revoked' && p.case?.state === 'approved' && (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => publishPack(p.id)}
                  >
                    Publish to portal
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="crm-muted">No pack attached yet.</p>
        )}
        {register.length > 0 ? (
          <div className="crm-pack-attach">
            <select id="crm-scenario-pick" defaultValue="">
              <option value="" disabled>
                Attach saved Decision scenario…
              </option>
              {register.map((f) => (
                <option key={f.scenarioId} value={f.scenarioId}>
                  {f.label} ({f.contentHash})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => {
                const el = document.getElementById('crm-scenario-pick');
                if (el?.value) attachFromRegister(el.value);
              }}
            >
              Attach pack
            </button>
          </div>
        ) : (
          <p className="crm-muted">
            No local scenarios yet — open <button type="button" className="linkish" onClick={() => { window.location.hash = 'decision'; }}>Decision</button> and freeze a case first.
          </p>
        )}
      </section>

      <section className="crm-section">
        <h3>Outreach</h3>
        <p className="crm-muted">
          Personalized from fit thesis. Use the agent (lower right) to research the firm, draft, or
          review copy — then send teaser email; invite to portal when they ask for numbers.
        </p>
        <div className="crm-agent-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              askAgent(
                `Research ${firm.firm_name} — wealth origin, energy / real-asset appetite, recent public context, and a Firebrand Eagle equity hook for ${selectedContact?.name || 'the primary contact'}.`,
              )
            }
          >
            Research firm
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              askAgent(
                `Draft a personalized teaser email for ${selectedContact?.name || 'the selected contact'} at ${firm.firm_name}. Use **Subject:** / **Body:** format.`,
              )
            }
          >
            Draft with agent
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              askAgent(
                'Review my outreach draft in livePageState.outreachDraft — tighten specificity and CA hygiene, then return an improved **Subject:** / **Body:**.',
              )
            }
          >
            Review draft
          </button>
        </div>
        <label className="crm-field">
          Subject
          <input
            value={draft.subject}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          />
        </label>
        <label className="crm-field">
          Body
          <textarea
            rows={8}
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          />
        </label>
        <div className="freeze-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !isAdmin || !publishedPack}
            onClick={sendOutreach}
          >
            Email outreach
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || !isAdmin || !publishedPack}
            onClick={invitePortal}
          >
            Invite / resend portal
          </button>
          {!isAdmin && <span className="crm-muted">Admin approval is required for external actions.</span>}
          {isAdmin && !publishedPack && (
            <span className="crm-muted">A current approved case must be published first.</span>
          )}
        </div>
        {(firm.portal_users || []).length > 0 && (
          <ul className="crm-portal-users">
            {firm.portal_users.map((u) => (
              <li key={u.id}>
                Portal: {u.name} &lt;{u.email}&gt;
                {u.last_login_at ? ` · last login ${u.last_login_at.slice(0, 10)}` : ' · invite pending'}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="crm-section">
        <h3>Activity &amp; knowledge</h3>
        <p className="crm-muted">
          Paste call notes or email text, or drop a transcript file. Content is chunked, embedded,
          and searchable across this firm — the assistant uses it too.
        </p>
        <div
          className={`crm-dropzone ${dragOver ? 'drag-over' : ''}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            ingestFiles(e.dataTransfer?.files);
          }}
        >
          <div className="crm-note-toolbar">
            <select
              value={noteKind}
              disabled={busy}
              onChange={(e) => setNoteKind(e.target.value)}
              aria-label="Activity type"
            >
              <option value="note">Note</option>
              <option value="email">Email / paste</option>
              <option value="transcript">Call transcript</option>
            </select>
            <input
              type="text"
              placeholder="Title (optional)"
              value={noteTitle}
              disabled={busy}
              onChange={(e) => setNoteTitle(e.target.value)}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={KB_ACCEPT}
              multiple
              hidden
              onChange={(e) => ingestFiles(e.target.files)}
            />
          </div>
          <textarea
            rows={5}
            placeholder="Drop a .txt / .vtt / .docx transcript here, or paste email / call notes…"
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
            onPaste={(e) => {
              const text = e.clipboardData?.getData('text') || '';
              if (text.length > 400 && noteKind === 'note') {
                setNoteKind(guessDocType('', text));
              }
            }}
          />
          <div className="crm-note-row crm-note-actions">
            <span className="crm-drop-hint">
              {dragOver ? 'Drop to index…' : 'txt, md, csv, json, vtt, srt, eml, docx · max 5MB'}
            </span>
            <button type="button" className="btn-primary" disabled={busy || !note.trim()} onClick={addNote}>
              {busy ? 'Indexing…' : 'Save & index'}
            </button>
          </div>
        </div>

        <div className="crm-kb-search">
          <label className="crm-field">
            Search knowledge
            <div className="crm-kb-search-row">
              <input
                type="search"
                placeholder="e.g. IRR concern, follow-up, Boswell…"
                value={kbQ}
                disabled={busy || kbSearching}
                onChange={(e) => setKbQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runKbSearch();
                  }
                }}
              />
              <button
                type="button"
                className="btn-secondary"
                disabled={busy || kbSearching || !kbQ.trim()}
                onClick={runKbSearch}
              >
                {kbSearching ? 'Searching…' : 'Search'}
              </button>
            </div>
          </label>
          {kbHits.length > 0 && (
            <ul className="crm-kb-hits">
              {kbHits.map((h) => (
                <li key={h.chunk_id}>
                  <div className="crm-activity-meta">
                    <span className="crm-activity-kind">{h.doc_type || 'doc'}</span>
                    <span>{h.title}</span>
                    <span>score {h.score}</span>
                  </div>
                  <p>{h.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {(firm.documents || []).length > 0 && (
          <div className="crm-doc-strip">
            <div className="crm-label">Indexed documents</div>
            <ul>
              {(firm.documents || []).slice(0, 8).map((d) => (
                <li key={d.id}>
                  <span className="crm-activity-kind">{d.doc_type}</span>
                  <strong>{d.title || d.filename || `Doc ${d.id}`}</strong>
                  <span>
                    {d.chunk_count || 0} chunks
                    {d.created_at ? ` · ${d.created_at.slice(0, 10)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="crm-activity-list">
          {(firm.activities || []).map((a) => (
            <li key={a.id}>
              <div className="crm-activity-meta">
                <span className="crm-activity-kind">{a.kind}</span>
                <span>{a.created_at?.replace('T', ' ').slice(0, 16)}</span>
                {a.actor_name && <span>{a.actor_name}</span>}
                {a.meta?.indexed && <span>indexed</span>}
              </div>
              {a.subject && <strong>{a.subject}</strong>}
              {a.body && <p>{a.body}</p>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default function CrmApp() {
  const { isAdmin, user } = useAuth();
  const [firms, setFirms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [firm, setFirm] = useState(null);
  const [filters, setFilters] = useState({ q: '', stage: '', tier: '', state: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [globalKbQ, setGlobalKbQ] = useState('');
  const [globalKbHits, setGlobalKbHits] = useState([]);
  const [globalKbSearching, setGlobalKbSearching] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        crmApi.listFirms({
          q: filters.q || undefined,
          stage: filters.stage || undefined,
          tier: filters.tier || undefined,
          state: filters.state || undefined,
        }),
        crmApi.summary(),
      ]);
      setFirms(list.firms || []);
      setSummary(sum);
    } catch (err) {
      setError(err.message || 'Failed to load CRM');
    }
  }, [filters]);

  const loadFirm = useCallback(async (id) => {
    if (!id) {
      setFirm(null);
      return;
    }
    try {
      setFirm(await crmApi.getFirm(id));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(loadList, filters.q ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadList, filters.q]);

  useEffect(() => {
    loadFirm(selectedId);
  }, [selectedId, loadFirm]);

  const onImport = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      // Seed is loaded server-side from the licensed runtime mount (ENERGY_FINTRX_SEED_PATH),
      // never a public static URL or a repo-committed file.
      const stats = await crmApi.importFintrxSeed();
      track('crm_imported', stats);
      setNotice(
        `Imported ${stats.created} new / ${stats.updated} updated firms (${stats.contacts_added} contacts).`,
      );
      await loadList();
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (body) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const created = await crmApi.createFirm(body);
      track('crm_firm_created', { firm_id: created.id, tier: created.tier });
      setNotice(`Created ${created.firm_name}.`);
      setSelectedId(created.id);
      await loadList();
      await loadFirm(created.id);
      return created;
    } catch (err) {
      setError(err.message || 'Create failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onGlobalKbSearch = async () => {
    const q = globalKbQ.trim();
    if (!q) return;
    setGlobalKbSearching(true);
    setError('');
    try {
      const res = await crmApi.searchKnowledge({ q, limit: 8 });
      setGlobalKbHits(res.hits || []);
      track('crm_knowledge_searched', { firm_id: null, q_len: q.length, hits: (res.hits || []).length });
    } catch (err) {
      setError(err.message);
    } finally {
      setGlobalKbSearching(false);
    }
  };

  return (
    <div className="crm-app">
      {error && <div className="auth-error crm-banner-error">{error}</div>}
      <div className="crm-shell">
        <FirmList
          firms={firms}
          selectedId={selectedId}
          onSelect={(id) => {
            setNotice('');
            setError('');
            setSelectedId(id);
            track('crm_firm_opened', { firm_id: id });
          }}
          filters={filters}
          setFilters={setFilters}
          summary={summary}
          onImport={onImport}
          onCreate={onCreate}
          busy={busy}
          kbQ={globalKbQ}
          setKbQ={setGlobalKbQ}
          kbHits={globalKbHits}
          onKbSearch={onGlobalKbSearch}
          kbSearching={globalKbSearching}
        />
        <FirmDetail
          firm={firm}
          isAdmin={isAdmin}
          userId={user?.id}
          notice={notice}
          setNotice={setNotice}
          setError={setError}
          onRefresh={async () => {
            await loadList();
            await loadFirm(selectedId);
          }}
          onDeleted={async () => {
            setSelectedId(null);
            setFirm(null);
            await loadList();
          }}
        />
      </div>
    </div>
  );
}
